import React, { memo, useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  Checkbox,
  Divider,
  FormControlLabel,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

const USER_TYPE_OPTIONS = [
  { key: "1", label: "User", minType: 1 },
  { key: "2", label: "Admin", minType: 1 },
  { key: "3", label: "Admin Account", minType: 4 },
  { key: "4", label: "Service Provider", minType: 5 },
  { key: "5", label: "Reseller", minType: 6 },
  { key: "6", label: "Distributor", minType: -1 },
];

const isUserTypeVisible = (option, userType) => {
  if (userType === -1) return true;
  if (option.key === "6") return false;
  if (option.key === "1" || option.key === "2") return true;
  if (option.key === "3") return userType > 3;
  if (option.key === "4") return userType > 4;
  if (option.key === "5") return userType > 5;
  return false;
};

const ChipToggle = memo(({ label, active, onClick, themeColor }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      px: 1.25,
      py: 0.5,
      borderRadius: "999px",
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1.4,
      border: "1px solid",
      borderColor: active ? themeColor : "rgba(15, 23, 42, 0.12)",
      bgcolor: active ? themeColor : "#fff",
      color: active ? "#fff" : "text.primary",
      transition: "background-color 0.15s, border-color 0.15s, color 0.15s",
      "&:hover": {
        borderColor: themeColor,
        bgcolor: active ? themeColor : `${themeColor}14`,
        opacity: active ? 0.92 : 1,
      },
    }}
  >
    {label}
  </ButtonBase>
));

ChipToggle.displayName = "ChipToggle";

/** Unified users list filter — button + popover (user type chips + searchable roles). */
const UsersFilterPanel = ({
  translate,
  themecolors,
  userType,
  roles,
  statusFilter,
  statusRoleFilter,
  countChecked,
  countCheckedRole,
  onUserTypeChange,
  onRoleChange,
  onClear,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [roleSearch, setRoleSearch] = useState("");
  const open = Boolean(anchorEl);
  const totalSelected = (countChecked || 0) + (countCheckedRole || 0);
  const themeColor = themecolors?.themeLightColor || "#1976d2";
  const isActive = open || totalSelected > 0;

  const userTypeOptions = useMemo(
    () => USER_TYPE_OPTIONS.filter((option) => isUserTypeVisible(option, userType)),
    [userType],
  );

  const visibleRoles = useMemo(() => {
    const q = roleSearch.trim().toLowerCase();
    return (roles || [])
      .filter((role) => userType === -1 || userType >= role.parentId)
      .filter((role) => !q || String(role.name || "").toLowerCase().includes(q));
  }, [roles, userType, roleSearch]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setRoleSearch("");
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
      <ButtonBase
        onClick={handleOpen}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.5,
          py: 0.75,
          height: 36,
          borderRadius: "10px",
          border: "1px solid",
          borderColor: themeColor,
          bgcolor: isActive ? themeColor : "#fff",
          color: isActive ? "#fff" : themeColor,
          fontSize: 13,
          fontWeight: 600,
          whiteSpace: "nowrap",
          transition: "border-color 0.15s, background-color 0.15s, color 0.15s",
          "&:hover": {
            borderColor: themeColor,
            bgcolor: isActive ? themeColor : `${themeColor}14`,
            opacity: isActive ? 0.92 : 1,
          },
        }}
      >
        <FilterListIcon sx={{ fontSize: 18, color: "inherit" }} />
        {translate?.("filter") || "Filters"}
        {totalSelected > 0 && (
          <Box
            component="span"
            sx={{
              minWidth: 20,
              height: 20,
              px: 0.6,
              borderRadius: "999px",
              bgcolor: isActive ? "#fff" : themeColor,
              color: isActive ? themeColor : "#fff",
              fontSize: 11,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {totalSelected}
          </Box>
        )}
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 320,
            maxWidth: "calc(100vw - 24px)",
            borderRadius: "12px",
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.14)",
            border: "1px solid rgba(15, 23, 42, 0.08)",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 1.75,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#f8fafc",
            borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {translate?.("filter") || "Filters"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {totalSelected > 0 && (
              <ButtonBase
                onClick={onClear}
                sx={{
                  px: 1,
                  py: 0.4,
                  borderRadius: "6px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: themeColor,
                  "&:hover": { bgcolor: `${themeColor}14` },
                }}
              >
                Clear
              </ButtonBase>
            )}
            <ButtonBase
              onClick={handleClose}
              aria-label="Close filters"
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                color: "text.secondary",
                "&:hover": { bgcolor: "rgba(15, 23, 42, 0.06)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </ButtonBase>
          </Box>
        </Box>

        <Box sx={{ px: 1.75, pt: 1.5, pb: 1 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "text.secondary",
              mb: 1,
            }}
          >
            User Type
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {userTypeOptions.map((option) => (
              <ChipToggle
                key={option.key}
                label={option.label}
                themeColor={themeColor}
                active={!!statusFilter[option.key]}
                onClick={() =>
                  onUserTypeChange(option.key, {
                    target: { checked: !statusFilter[option.key] },
                  })
                }
              />
            ))}
          </Box>
        </Box>

        <Divider sx={{ mx: 1.5 }} />

        <Box sx={{ px: 1.75, pt: 1.25, pb: 1.5 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "text.secondary",
              mb: 1,
            }}
          >
            Role
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Search roles…"
            value={roleSearch}
            onChange={(e) => setRoleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                bgcolor: "#fff",
                fontSize: 13,
              },
            }}
          />
          <Box
            sx={{
              maxHeight: 220,
              overflowY: "auto",
              pr: 0.5,
              display: "flex",
              flexDirection: "column",
              gap: 0.15,
            }}
          >
            {visibleRoles.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "text.secondary", py: 1.5 }}>
                No roles found
              </Typography>
            ) : (
              visibleRoles.map((role) => (
                <FormControlLabel
                  key={role.id}
                  sx={{
                    m: 0,
                    px: 0.5,
                    py: 0.15,
                    borderRadius: "6px",
                    "&:hover": { bgcolor: "rgba(15, 23, 42, 0.04)" },
                    "& .MuiFormControlLabel-label": { fontSize: 13 },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={!!statusRoleFilter[role.id]}
                      onChange={(e) => onRoleChange(String(role.id), e)}
                      sx={{
                        color: themeColor,
                        "&.Mui-checked": { color: themeColor },
                      }}
                    />
                  }
                  label={role.name}
                />
              ))
            )}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default UsersFilterPanel;

// Keep named exports for any leftover imports (thin wrappers).
export const UserTypeFilter = memo(() => null);
export const RolesFilter = memo(() => null);
